import type { MealPlanDayMenu, WeeklyBooking } from '../types';
import { dayKeysForWeek, mealPeriodText } from './mealPlanWeek';

export interface MealPlanPrepCountRow {
  dayKey: string;
  period: string;
  menuItemId: string;
  menuItemName: string;
  booked: number;
  redeemed: number;
  cancelled: number;
  noShow: number;
}

export interface MealPlanWeekStats {
  booked: number;
  cancelled: number;
  redeemed: number;
  noShow: number;
  cancellationRate: number;
  noShowRate: number;
}

export function summarizeWeekBookings(bookings: WeeklyBooking[]): MealPlanWeekStats {
  let booked = 0;
  let cancelled = 0;
  let redeemed = 0;
  let noShow = 0;
  for (const b of bookings) {
    if (b.status === 'booked') booked += 1;
    else if (b.status === 'cancelled') cancelled += 1;
    else if (b.status === 'redeemed') redeemed += 1;
    else if (b.status === 'no_show') noShow += 1;
  }
  const denomCancel = booked + cancelled + redeemed + noShow;
  const servedOrMissed = redeemed + noShow;
  return {
    booked,
    cancelled,
    redeemed,
    noShow,
    cancellationRate: denomCancel > 0 ? cancelled / denomCancel : 0,
    noShowRate: servedOrMissed > 0 ? noShow / servedOrMissed : 0,
  };
}

/** Prep counts from bookings and the written meal for that day. One row per meal, not per priced dish. */
export function buildPrepReport(
  weekId: string,
  bookings: WeeklyBooking[],
  menu: Record<string, MealPlanDayMenu | {
    title?: string;
    breakfastText?: string;
    lunchText?: string;
    dinnerText?: string;
    breakfast?: { name?: string }[];
    lunch?: { name?: string }[];
    dinner?: { name?: string }[];
  }>,
): MealPlanPrepCountRow[] {
  const rows: MealPlanPrepCountRow[] = [];
  for (const dayKey of dayKeysForWeek(weekId)) {
    for (const period of ['breakfast', 'lunch', 'dinner'] as const) {
      const dayBookings = bookings.filter((b) => b.dayKey === dayKey && b.period === period);
      const mealName = mealPeriodText(menu[dayKey], period);
      const booked = dayBookings.filter((b) => b.status === 'booked' || b.status === 'redeemed').length;
      const redeemed = dayBookings.filter((b) => b.status === 'redeemed').length;
      const cancelled = dayBookings.filter((b) => b.status === 'cancelled').length;
      const noShow = dayBookings.filter((b) => b.status === 'no_show').length;
      if (!mealName) {
        if (dayBookings.length > 0) {
          rows.push({
            dayKey,
            period,
            menuItemId: '_covers',
            menuItemName: '(covers — no meal written)',
            booked,
            redeemed,
            cancelled,
            noShow,
          });
        }
        continue;
      }
      rows.push({
        dayKey,
        period,
        menuItemId: `_${period}`,
        menuItemName: mealName,
        booked,
        redeemed,
        cancelled,
        noShow,
      });
    }
  }
  return rows;
}

export interface MealPlanCostBreakdown {
  costPerMealCents: number;
  effectivePricePerMealCents: number;
  marginPerMealCents: number;
  breakEvenUptake: number;
  weeklyFoodCostCents: number;
  wasteCostCents: number;
}

export function computeMealPlanCostAnalysis(input: {
  foodCostPerMealCents: number;
  laborCostPerMealCents: number;
  overheadPercent: number;
  planPriceCents: number;
  mealsPerWeek: number;
  bookedMeals: number;
  noShowMeals: number;
}): MealPlanCostBreakdown {
  const base = input.foodCostPerMealCents + input.laborCostPerMealCents;
  const costPerMealCents = Math.round(base * (1 + Math.max(0, input.overheadPercent) / 100));
  const meals = Math.max(1, input.mealsPerWeek);
  const effectivePricePerMealCents = Math.round(input.planPriceCents / meals);
  const marginPerMealCents = effectivePricePerMealCents - costPerMealCents;
  const breakEvenUptake =
    effectivePricePerMealCents > 0 ? Math.min(1, costPerMealCents / effectivePricePerMealCents) : 1;
  return {
    costPerMealCents,
    effectivePricePerMealCents,
    marginPerMealCents,
    breakEvenUptake,
    weeklyFoodCostCents: input.bookedMeals * costPerMealCents,
    wasteCostCents: input.noShowMeals * costPerMealCents,
  };
}
